import React from 'react';
import { Play, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '@/lib/store';

function relTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  return 'Yesterday';
}

export default function RecentRuns() {
  const { state, loadRun, removeFromHistory, clearHistory } = useStore();

  if (!state.runHistory || state.runHistory.length === 0) return null;

  return (
    <section id="recent-runs" data-testid="recent-runs-strip" className="py-4">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-white/40">Recent Runs</span>
          <button
            className="text-xs text-white/50 hover:text-[#F2704C] transition-colors"
            onClick={() => { clearHistory(); toast.success('Run history cleared.'); }}
            data-testid="clear-history-btn"
          >
            Clear History
          </button>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
          {state.runHistory.map((run) => (
            <div
              key={run.timestamp}
              className="glass-panel-soft p-3.5 min-w-[280px] flex-1 max-w-[340px] cursor-pointer group relative hover:border-white/15 transition-colors"
              onClick={() => { loadRun(run); toast.success('Run loaded', { description: run.source }); }}
              data-testid={`run-card-${run.timestamp}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="tag-pill"
                  style={{ background: `${run.categoryColor}22`, color: run.categoryColor, border: `1px solid ${run.categoryColor}44` }}
                >
                  {run.category}
                </span>
                {run.version && run.version > 0 && (
                  <span className="tag-pill bg-white/5 text-white/60 border border-white/10">v{run.version}</span>
                )}
                <span className="ml-auto tag-pill bg-[#7EE06D]/15 text-[#7EE06D] border border-[#7EE06D]/30">✓ Done</span>
              </div>
              <div className="text-sm font-medium text-white truncate">{run.source}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-mono text-xs text-white/45">{run.duration}</span>
                <span className="text-xs text-white/40">{relTime(run.timestamp)}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-7 w-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-white/10">
                    <Play className="h-3 w-3 text-white/80" />
                  </div>
                  <span className="text-xs text-white/50 group-hover:text-white/70 transition-colors">Load results</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeFromHistory(run.timestamp); toast.success('Run removed from history'); }}
                  className="h-7 w-7 rounded-full flex items-center justify-center text-[#F2704C]/75 hover:text-[#F2704C] hover:bg-[#F2704C]/10 border border-transparent hover:border-[#F2704C]/25 transition-colors"
                  aria-label="Remove from history"
                  data-testid={`remove-run-${run.timestamp}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
